import { AppState, Auth0Provider } from '@auth0/auth0-react';
import React, { createContext, Reducer, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

import addAuthHeaderInGraph from 'graphql/apollo/addAuthHeader';
import { START_FULL_PATH } from 'routes/constants';

import AuthSession from './AuthSession';

export interface AuthState {
  token: string | null;
  type?: 'auth0' | 'anon' | 'none';
}

interface AuthProviderProps {
  children: React.ReactNode;
}

type Actions =
  | { token: string; type: 'auth0' }
  | { token: string; type: 'anon' }
  | { type: 'none' };

interface AuthContextProps {
  auth: AuthState;
  authDispatch: React.Dispatch<Actions>;
}

const initialState: AuthState = { token: null };

export const AuthContext = createContext<AuthContextProps>({
  auth: initialState,
  authDispatch: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
});

const authReducer: Reducer<AuthState, Actions> = (state = initialState, action) => {
  switch (action.type) {
    case 'auth0':
    case 'anon': {
      return { ...state, ...action };
    }
    case 'none':
      return { ...state, type: 'none', token: null };
    default:
      return { ...state };
  }
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const [auth, authDispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    addAuthHeaderInGraph(auth);
  }, [auth]);

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo ?? window.location.pathname, {
      replace: true,
    });
  };

  return (
    <Auth0Provider
      domain={process.env.AUTH0_CLIENT_DOMAIN!}
      clientId={process.env.AUTH0_CLIENT_ID!}
      redirectUri={`${window.location.origin}${START_FULL_PATH}`}
      onRedirectCallback={onRedirectCallback}
    >
      <AuthContext.Provider value={{ auth, authDispatch }}>
        <AuthSession>{children}</AuthSession>
      </AuthContext.Provider>
    </Auth0Provider>
  );
};

export default AuthProvider;
